import { useState } from 'react';
import { formatModifier } from '@character-creator/core';
import { clsx } from 'clsx';
import type { Race, Subrace } from '@character-creator/types';
import { useRulesetRaces } from '../hooks/useRulesetData';
import { useRulesetTerminology } from '../hooks/useRulesetFeatures';
import { LinkedText } from '../components/LinkedText';

export function BrowseRacesPage() {
  const [selectedRace, setSelectedRace] = useState<Race | null>(null);
  const [selectedSubrace, setSelectedSubrace] = useState<Subrace | null>(null);
  const races = useRulesetRaces();
  const terminology = useRulesetTerminology();

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold text-text-primary mb-2">
          Playable {terminology.racePlural}
        </h1>
        <p className="text-text-secondary">
          Choose from {races.length} {terminology.racePlural.toLowerCase()} available
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Race List */}
        <div className="lg:col-span-1 space-y-3">
          {races.map((race) => (
            <button
              id={race.id}
              key={race.id}
              onClick={() => {
                setSelectedRace(race);
                setSelectedSubrace(null);
              }}
              className={clsx(
                'w-full text-left card card-hover p-4 transition-all',
                selectedRace?.id === race.id && 'ring-2 ring-accent-gold'
              )}
            >
              <h3 className="font-display font-semibold text-text-primary">
                {race.name}
              </h3>
              <p className="text-sm text-text-muted mt-1">
                {race.abilityBonuses.map((b) => (
                  <span key={b.ability} className="mr-2">
                    {b.ability.slice(0, 3).toUpperCase()} {formatModifier(b.bonus)}
                  </span>
                ))}
              </p>
            </button>
          ))}
        </div>

        {/* Race Details */}
        <div className="lg:col-span-2">
          {selectedRace ? (
            <RaceDetails
              race={selectedRace}
              selectedSubrace={selectedSubrace}
              onSelectSubrace={setSelectedSubrace}
            />
          ) : (
            <div className="card p-8 text-center text-text-muted">
              <p>Select a {terminology.race.toLowerCase()} to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RaceDetails({
  race,
  selectedSubrace,
  onSelectSubrace,
}: {
  race: Race;
  selectedSubrace: Subrace | null;
  onSelectSubrace: (subrace: Subrace | null) => void;
}) {
  const terminology = useRulesetTerminology();

  return (
    <div className="card p-6 space-y-6 animate-fade-in">
      <div>
        <h2 className="font-display text-2xl font-bold text-text-primary">
          {race.name}
        </h2>
        <p className="text-text-secondary mt-2">
          <LinkedText excludePages={['races']}>{race.description}</LinkedText>
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatBlock label="Size" value={race.size} />
        <StatBlock label="Speed" value={`${race.speed} ft.`} />
        <StatBlock
          label="Languages"
          value={race.languages.join(', ')}
        />
        {race.languageChoice && (
          <StatBlock
            label="Extra Languages"
            value={`+${race.languageChoice}`}
          />
        )}
      </div>

      {/* Ability Bonuses */}
      <div>
        <h3 className="font-display font-semibold text-text-primary mb-3">
          Ability Score Increases
        </h3>
        <div className="flex flex-wrap gap-2">
          {race.abilityBonuses.map((bonus) => (
            <span
              key={bonus.ability}
              className="px-3 py-1 bg-accent-gold/10 text-accent-gold rounded-full text-sm font-medium"
            >
              {bonus.ability.charAt(0).toUpperCase() + bonus.ability.slice(1)}{' '}
              {formatModifier(bonus.bonus)}
            </span>
          ))}
          {selectedSubrace?.abilityBonuses.map((bonus) => (
            <span
              key={bonus.ability}
              className="px-3 py-1 bg-accent-bronze/10 text-accent-bronze rounded-full text-sm font-medium"
            >
              {bonus.ability.charAt(0).toUpperCase() + bonus.ability.slice(1)}{' '}
              {formatModifier(bonus.bonus)}
            </span>
          ))}
        </div>
      </div>

      {/* Subraces/Lineages */}
      {race.subraces && race.subraces.length > 0 && (
        <div>
          <h3 className="font-display font-semibold text-text-primary mb-3">
            {terminology.subracePlural}
          </h3>
          <div className="flex flex-wrap gap-2">
            {race.subraces.map((subrace) => (
              <button
                key={subrace.id}
                onClick={() =>
                  onSelectSubrace(
                    selectedSubrace?.id === subrace.id ? null : subrace
                  )
                }
                className={clsx(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  selectedSubrace?.id === subrace.id
                    ? 'bg-accent-gold text-background-primary'
                    : 'bg-background-elevated text-text-secondary hover:text-text-primary'
                )}
              >
                {subrace.name}
              </button>
            ))}
          </div>
          {selectedSubrace && (
            <div className="mt-4 p-4 bg-background-secondary rounded-lg animate-fade-in">
              <h4 className="font-medium text-text-primary mb-2">
                {selectedSubrace.name}
              </h4>
              <p className="text-sm text-text-secondary">
                <LinkedText excludePages={['races']}>{selectedSubrace.description}</LinkedText>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Traits */}
      <div>
        <h3 className="font-display font-semibold text-text-primary mb-3">
          {terminology.race} Traits
        </h3>
        <div className="space-y-3">
          {race.traits.map((trait) => (
            <div key={trait.id} className="p-3 bg-background-secondary rounded-lg">
              <h4 className="font-medium text-text-primary">{trait.name}</h4>
              <p className="text-sm text-text-secondary mt-1">
                <LinkedText excludePages={['races']}>{trait.description}</LinkedText>
              </p>
            </div>
          ))}
          {selectedSubrace?.traits.map((trait) => (
            <div
              key={trait.id}
              className="p-3 bg-background-secondary rounded-lg border-l-2 border-accent-bronze"
            >
              <h4 className="font-medium text-text-primary">{trait.name}</h4>
              <p className="text-sm text-text-secondary mt-1">
                <LinkedText excludePages={['races']}>{trait.description}</LinkedText>
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Age */}
      <div>
        <h3 className="font-display font-semibold text-text-primary mb-2">
          Age
        </h3>
        <p className="text-sm text-text-secondary">{race.age}</p>
      </div>
    </div>
  );
}

function StatBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 bg-background-secondary rounded-lg">
      <div className="text-xs text-text-muted uppercase tracking-wide">
        {label}
      </div>
      <div className="font-medium text-text-primary capitalize">{value}</div>
    </div>
  );
}
